/**
 * Deterministic, serializable PRNG (mulberry32). The whole game — problem
 * generation, choice shuffling — draws from one Rng whose `state` is stored in
 * GameState, so a run can be replayed exactly on the server from (seed, events).
 *
 * Never use Math.random() anywhere in packages/core.
 */
export class Rng {
  constructor(public state: number) {
    this.state = state >>> 0;
  }

  /** Uniform float in [0, 1). */
  next(): number {
    let t = (this.state = (this.state + 0x6d2b79f5) >>> 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform integer in [lo, hi] inclusive. */
  int(lo: number, hi: number): number {
    if (hi < lo) throw new Error(`Rng.int: empty range [${lo}, ${hi}]`);
    return lo + Math.floor(this.next() * (hi - lo + 1));
  }

  /** Uniform integer in [lo, hi] inclusive, excluding the given values. */
  intExcept(lo: number, hi: number, except: readonly number[]): number {
    const pool: number[] = [];
    for (let v = lo; v <= hi; v++) if (!except.includes(v)) pool.push(v);
    if (!pool.length) throw new Error(`Rng.intExcept: nothing left in [${lo}, ${hi}]`);
    return pool[Math.floor(this.next() * pool.length)]!;
  }

  chance(p: number): boolean {
    return this.next() < p;
  }

  pick<T>(items: readonly T[]): T {
    if (!items.length) throw new Error('Rng.pick: empty array');
    return items[Math.floor(this.next() * items.length)]!;
  }

  /** Weighted pick; weights need not sum to 1. */
  weighted<T>(items: readonly { item: T; weight: number }[]): T {
    const total = items.reduce((s, x) => s + x.weight, 0);
    let r = this.next() * total;
    for (const x of items) {
      r -= x.weight;
      if (r < 0) return x.item;
    }
    return items[items.length - 1]!.item;
  }

  /** Returns a new shuffled array (Fisher–Yates). */
  shuffle<T>(items: readonly T[]): T[] {
    const a = items.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      [a[i], a[j]] = [a[j]!, a[i]!];
    }
    return a;
  }

  /** ±1 with equal probability. */
  sign(): 1 | -1 {
    return this.next() < 0.5 ? 1 : -1;
  }
}

/** Derive an independent 32-bit seed from a seed and a salt (for sub-streams). */
export function deriveSeed(seed: number, salt: number): number {
  let h = (seed ^ Math.imul(salt + 0x9e3779b9, 0x85ebca6b)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
}
